<template>
  <v-app>
    <v-progress-linear
      indeterminate
      color="primary"
      v-show="loading"
      height="6"
    ></v-progress-linear>
    <vue-splash
      :show="loading"
      :title="intro_text"
      color="#00bfa5"
      :logo="logo"
      :size="500"
      :fixed="false"
    />
    <v-snackbar
      v-model="newVersion"
      :timeout="-1"
      bottom
      right
      color="primary"
      class="new-version-snackbar"
    >
      A new version of {{ appTitle }} is available.
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="reloadPage">Reload</v-btn>
        <v-btn icon v-bind="attrs" @click="newVersion = false" aria-label="Dismiss">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
    <v-fade-transition>
      <v-row no-gutters v-show="loadmain">
        <home-app-bar v-if="!$route.meta.bare" :name="$route.name" v-model="drawer" />
        <v-main class="fill-height" fluid>
          <private-page background="ngc2244.jpg">
            <router-view />
          </private-page>
        </v-main>
      </v-row>
    </v-fade-transition>
  </v-app>
</template>

<script>
import { useUserStore } from "@/store/user";
import { API } from "./axios-config";
import { NEW_VERSION_EVENT } from "./utils/versionWatch";

export default {
  name: "App",
  components: {
    HomeAppBar: () => import("@/layouts/AppBar"),
    PrivatePage: () => import("@/components/PrivatePage"),
  },
  data() {
    return {
      drawer: true,
      isLoggedIn: false,
      loading: false,
      loadmain: true,
      newVersion: false,
    };
  },
  computed: {
    logo() {
      return require("@/assets/splash1.png");
    },
    intro_text() {
      return process.env.VUE_APP_TITLE;
    },
    appTitle() {
      return process.env.VUE_APP_TITLE || "CAMCEADHUB";
    },
  },
  methods: {
    showNewVersion() {
      this.newVersion = true;
    },
    reloadPage() {
      window.location.reload();
    },
  },
  beforeDestroy() {
    window.removeEventListener(NEW_VERSION_EVENT, this.showNewVersion);
  },
  mounted() {
    window.addEventListener(NEW_VERSION_EVENT, this.showNewVersion);
    if (this.userStore.isLoggedIn()) {
      this.loading = true;
      this.loadmain = false;
      setTimeout(() => {
        this.loading = false;
      }, 2000);
      setTimeout(() => {
        this.loadmain = true;
      }, 2500);
    } else {
      this.loading = false;
      this.loadmain = true;
    }
    API.get(`/api/version/`)
      .then((response) => {
        console.log(response);
      })
      .catch((error) => {
        console.log(error);
      });
    // Change favicon
    // window.document.head
    //   .querySelectorAll("link[rel*='icon']")
    //   .forEach((el) => (el.href = `/favicon.ico`));
    this.isLoggedIn = this.userStore.isLoggedIn();
  },
  setup() {
    const userStore = useUserStore();
    return { userStore };
  },
  created() {
    document.title = process.env.VUE_APP_TITLE;
  },
};
</script>

<style lang="scss">
// Plain v-cards don't set their own background/border - on the dark theme's
// near-black surface color they'd otherwise blend into the (also dark)
// starry page background behind them (e.g. Jupyter servers list, admin
// pages). Give every dark-themed card a subtle outline so it stays visible.
.theme--dark.v-card {
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.web-000 {
  background-image: linear-gradient(
    to top,
    white 0%,
    var(--v-primary-base) 80%,
    var(--v-primary-base) 100%
  );
}

.web-094 {
  background-image: linear-gradient(
    to top,
    #f3e7e9 0%,
    #e3eeff 99%,
    #e3eeff 100%
  );
}

.web-134 {
  background-image: linear-gradient(to top, #dfe9f3 0%, white 100%);
}

.web-010 {
  background-image: linear-gradient(120deg, #a1c4fd 0%, #c2e9fb 100%);
}

.web-018 {
  background-image: linear-gradient(120deg, #fdfbfb 0%, #ebedee 100%);
}

.web-026 {
  background-image: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.web-045 {
  background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.15) 0%,
      rgba(0, 0, 0, 0.15) 100%
    ),
    radial-gradient(
        at top center,
        rgba(255, 255, 255, 0.4) 0%,
        rgba(0, 0, 0, 0.4) 120%
      )
      #989898;
  background-blend-mode: multiply, multiply;
}

.web-074 {
  background: #c9ccd3;
  background-image: linear-gradient(
    -180deg,
    rgba(255, 255, 255, 0.5) 0%,
    rgba(0, 0, 0, 0.5) 100%
  );
  background-blend-mode: lighten;
}

.web-119 {
  background-color: #cddcdc;
  background-image: radial-gradient(
      at 50% 100%,
      rgba(255, 255, 255, 0.5) 0%,
      rgba(0, 0, 0, 0.5) 100%
    ),
    linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(0, 0, 0, 0.25) 100%
    );
  background-blend-mode: screen, overlay;
}

.web-130 {
  background: linear-gradient(to bottom, #d5dee7 0%, #e8ebf2 50%, #e2e7ed 100%),
    linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.02) 50%,
      rgba(255, 255, 255, 0.02) 61%,
      rgba(0, 0, 0, 0.02) 73%
    ),
    linear-gradient(33deg, rgba(255, 255, 255, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%);
  background-blend-mode: normal, color-burn;
}

.web-134 {
  background-image: linear-gradient(to top, #dfe9f3 0%, white 100%);
}

.web-152 {
  background-image: linear-gradient(
    -225deg,
    #2cd8d5 0%,
    #c5c1ff 56%,
    #ffbac3 100%
  );
}

.web-159 {
  background-image: linear-gradient(
    -225deg,
    #22e1ff 0%,
    #1d8fe1 48%,
    #625eb1 100%
  );
}

.web-163 {
  background-image: linear-gradient(-225deg, #7de2fc 0%, #b9b6e5 100%);
}

.web-166 {
  background-image: linear-gradient(
    -225deg,
    #7085b6 0%,
    #87a7d9 50%,
    #def3f8 100%
  );
}
</style>